import { useEffect, useRef, useState } from 'react';
import { serverNow, teamCount, wheelMoment, wheelRotation, wheelSlice } from './wheelModel';
import './wheel.css';

export function useWheelMoment(wheel, clock) {
  const [, render] = useState(0);
  useEffect(() => {
    let previous = '';
    const update = () => {
      const moment = wheelMoment(wheel, serverNow(clock));
      const key = `${moment.index}:${moment.phase}`;
      if (key !== previous) { previous = key; render((value) => value + 1); }
    };
    update();
    const timer = setInterval(update, 100);
    return () => clearInterval(timer);
  }, [wheel, clock]);
  return wheelMoment(wheel, serverNow(clock));
}

export default function DrawWheel({ draw, moment, clock, online, spinControl }) {
  const disk = useRef(null);
  const wheel = draw.wheel;
  const remaining = draw.pool.filter((name) => !draw.slots.some((group) => group.includes(name)));
  const showing = moment.active;
  const teams = showing ? moment.teams : remaining;
  const slices = teams.length ? teams : ['NOVA DOTA 2'];
  const reveal = showing && ['result', 'confirming'].includes(moment.phase);
  const selected = showing ? moment.step.team : null;
  const complete = draw.phase === 'complete';
  const angle = selected ? wheelRotation(slices, selected) : 0;
  const spinId = showing ? `${wheel.id}:${moment.index}` : 'idle';
  useEffect(() => {
    const element = disk.current;
    if (!element || !showing) return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    let animation;
    const sync = () => {
      const elapsed = serverNow(clock) - moment.startsAt;
      if (media.matches) {
        animation?.cancel(); animation = null;
        element.style.transform = `rotate(${elapsed >= wheel.spinMs ? angle : 0}deg)`;
      } else {
        if (!animation) {
          element.style.transform = '';
          animation = element.animate([{ transform: 'rotate(0deg)' }, { transform: `rotate(${angle}deg)` }],
            { duration: wheel.spinMs, easing: 'cubic-bezier(.12,.76,.16,1)', fill: 'both' });
        }
        if (Math.abs((animation.currentTime || 0) - elapsed) > 100) animation.currentTime = elapsed;
      }
    };
    sync();
    const timer = setInterval(sync, 250);
    media.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    return () => { clearInterval(timer); animation?.cancel(); element.style.transform = ''; media.removeEventListener('change', sync); document.removeEventListener('visibilitychange', sync); };
  }, [spinId, angle, showing, moment.startsAt, wheel?.spinMs, clock]);
  return <section className={`draw-wheel-panel${reveal ? ' is-revealed' : ''}`} aria-label="Колесо жеребьёвки" data-phase={moment.phase} data-spin={spinId}>
    <header><span className="draw-kicker">{complete ? 'ЖЕРЕБЬЁВКА ЗАВЕРШЕНА' : 'СЛЕДУЮЩАЯ КОМАНДА'}</span><span className="draw-wheel-badge">{showing && wheel.automatic ? 'АВТО' : 'LIVE DRAW'}</span></header>
    <div className="draw-wheel-graphic">
      <svg viewBox="0 0 520 520" role="img" aria-label={`Колесо: ${teams.length} команд`}>
        <circle className="draw-wheel-aura" cx="260" cy="260" r="248" />
        <circle className="draw-wheel-rim" cx="260" cy="260" r="235" />
        <g className="draw-wheel-disk" ref={disk}>
          {slices.map((team, index) => <g key={team}>
            <path className={`draw-wheel-slice slice-${index % 4}${reveal && team === selected ? ' is-winner' : ''}`} d={wheelSlice(index, slices.length)} fillRule="evenodd" />
            <g transform={`rotate(${-90 + index * 360 / slices.length} 260 260)`}>
              <text x="467" y="260" dy=".35em" textAnchor="end" className="draw-wheel-name"><title>{team}</title>{team.length > 21 ? `${team.slice(0, 20)}…` : team}</text>
            </g>
          </g>)}
        </g>
        {Array.from({ length: 60 }, (_, i) => <circle key={i} className="draw-wheel-tick" cx={260 + 240 * Math.cos(i * Math.PI / 30)} cy={260 + 240 * Math.sin(i * Math.PI / 30)} r={i % 5 === 0 ? 2.5 : 1} />)}
        <circle className="draw-wheel-hub" cx="260" cy="260" r="66" />
        <image href="/assets/logo-federation.png" x="213" y="213" width="94" height="94" />
        <path className="draw-wheel-pointer" d="M246 13H274L260 44Z" />
      </svg>
    </div>
    <div className="draw-wheel-result" aria-live="polite" aria-atomic="true">
      <span>{reveal ? moment.applied ? 'КОМАНДА ОПРЕДЕЛЕНА' : 'ФИКСИРУЕМ РЕЗУЛЬТАТ' : complete ? 'ВСЕ МЕСТА ЗАНЯТЫ' : moment.phase === 'spinning' ? 'КОЛЕСО ВРАЩАЕТСЯ' : moment.phase === 'ready' ? 'НАЧИНАЕМ ВРАЩЕНИЕ' : 'ГОТОВЫ К ЖЕРЕБЬЁВКЕ'}</span>
      <h2 title={reveal ? selected : undefined}>{reveal ? selected : complete ? 'Группы сформированы' : `${teamCount(remaining.length)} в колесе`}</h2>
      <p>{reveal ? <>ГРУППА <b>{moment.step.group + 1}</b><i />МЕСТО <b>{moment.step.slot + 1}</b></> : complete ? 'Все пары — в расписании матчей' : 'После остановки команда займёт место в группе'}</p>
      {spinControl && <button type="button" className="draw-spin-now" disabled={spinControl.disabled} onClick={spinControl.onSpin}><span aria-hidden="true">↻</span>{spinControl.label}</button>}
      {spinControl?.error && <small className="draw-spin-error" role="alert">{spinControl.error}</small>}
    </div>
    {!online && <small className="draw-wheel-connection">Нет связи · ждём подтверждение результата</small>}
  </section>;
}
